import { type ReactNode } from 'react';
import SearchBar from './SearchBar';
import EntityList from './EntityList';
import DetailPanel from './DetailPanel';
import Participants from './Participants';
import TimelineStrip from './TimelineStrip';
import NetworkGraph from './views/NetworkGraph';
import DocumentViewer from './views/DocumentViewer';
import PersonExplorer from './views/PersonExplorer';
import FlightMap from './views/FlightMap';
import MoneyFlow from './views/MoneyFlow';
import type { ViewType, RoomState } from '../party';
import type { LayoutMode } from '../hooks/useLayout';
import type { DiscordAuth } from '../discord';

interface LayoutProps {
  layout: { mode: LayoutMode; width: number; height: number };
  view: ViewType;
  onChangeView: (view: ViewType) => void;
  selectedSlug: string | null;
  selectedType: string;
  onSelectEntity: (type: string, id: string) => void;
  party: {
    state: RoomState;
    changeView: (v: ViewType) => void;
    selectEntity: (e: { type: string; id: string } | null) => void;
    toggleLead: () => void;
    addAnnotation: (t: string, id: string, note: string) => void;
    updateFilters: (f: Record<string, any>) => void;
  };
  auth: DiscordAuth | null;
}

const VIEW_LABELS: Record<ViewType, string> = {
  graph: 'Network',
  documents: 'Documents',
  persons: 'Persons',
  flights: 'Flights',
  money: 'Money',
};

const VIEW_ICONS: Record<ViewType, string> = {
  graph: '\u{1F578}',
  documents: '\u{1F4C4}',
  persons: '\u{1F464}',
  flights: '\u{2708}',
  money: '\u{1F4B0}',
};

export default function Layout({
  layout, view, onChangeView, selectedSlug, selectedType,
  onSelectEntity, party, auth,
}: LayoutProps) {
  const isMobile = layout.mode === 'mobile';
  const isTablet = layout.mode === 'tablet';

  const mainView = renderMainView(view, selectedSlug, selectedType, onSelectEntity, party);

  if (isMobile) {
    return (
      <div className="layout-mobile">
        <div className="mobile-header">
          <SearchBar onSelect={onSelectEntity} compact />
        </div>
        <div className="mobile-tabs">
          {(Object.keys(VIEW_LABELS) as ViewType[]).map((v) => (
            <button
              key={v}
              className={`tab ${view === v ? 'active' : ''}`}
              onClick={() => onChangeView(v)}
            >
              <span className="tab-icon">{VIEW_ICONS[v]}</span>
              <span className="tab-label">{VIEW_LABELS[v]}</span>
            </button>
          ))}
        </div>
        <div className="mobile-main">{mainView}</div>
        {selectedSlug && (
          <div className="mobile-detail-sheet">
            <DetailPanel
              slug={selectedSlug}
              type={selectedType}
              onNavigate={onSelectEntity}
              onClose={() => onSelectEntity('', '')}
            />
          </div>
        )}
        <Participants
          participants={party.state.participants}
          leader={party.state.leader}
          currentUserId={auth?.user.id || ''}
          onToggleLead={party.toggleLead}
          compact
        />
      </div>
    );
  }

  return (
    <div className="layout-desktop">
      <div className="top-bar">
        <SearchBar onSelect={onSelectEntity} />
        <div className="view-tabs">
          {(Object.keys(VIEW_LABELS) as ViewType[]).map((v) => (
            <button
              key={v}
              className={`tab ${view === v ? 'active' : ''}`}
              onClick={() => onChangeView(v)}
            >
              <span className="tab-icon">{VIEW_ICONS[v]}</span>
              {VIEW_LABELS[v]}
            </button>
          ))}
        </div>
      </div>
      <div className="panels">
        {!isTablet && (
          <div className="panel-left">
            <EntityList
              view={view}
              onSelect={onSelectEntity}
              selectedId={selectedSlug}
              filters={party.state.filters}
              onUpdateFilters={party.updateFilters}
            />
          </div>
        )}
        <div className="panel-main">
          {mainView}
        </div>
        {selectedSlug && (
          <div className="panel-right">
            <DetailPanel
              slug={selectedSlug}
              type={selectedType}
              onNavigate={onSelectEntity}
              onClose={() => onSelectEntity('', '')}
            />
          </div>
        )}
      </div>
      <TimelineStrip
        onRangeChange={(from, to) => party.updateFilters({ ...party.state.filters, date_from: from, date_to: to })}
      />
      <Participants
        participants={party.state.participants}
        leader={party.state.leader}
        currentUserId={auth?.user.id || ''}
        onToggleLead={party.toggleLead}
      />
    </div>
  );
}

function renderMainView(
  view: ViewType,
  selectedSlug: string | null,
  selectedType: string,
  onSelectEntity: (type: string, id: string) => void,
  party: LayoutProps['party'],
) {
  switch (view) {
    case 'graph':
      return (
        <NetworkGraph
          onSelectPerson={(slug) => onSelectEntity('person', slug)}
          selectedSlug={selectedSlug}
          participants={party.state.participants}
        />
      );
    case 'documents':
      return (
        <DocumentViewer
          onSelectDocument={(id) => onSelectEntity('document', id)}
          selectedId={selectedSlug}
          filters={party.state.filters}
        />
      );
    case 'persons':
      return (
        <PersonExplorer
          onSelectPerson={(slug) => onSelectEntity('person', slug)}
          selectedSlug={selectedSlug}
          filters={party.state.filters}
        />
      );
    case 'flights':
      return (
        <FlightMap
          onSelectFlight={(id) => onSelectEntity('flight', id)}
          filters={party.state.filters}
        />
      );
    case 'money':
      return (
        <MoneyFlow
          onSelectEntity={(id) => onSelectEntity('entity', id)}
          filters={party.state.filters}
        />
      );
  }
}
